import type { ReactNode } from 'react';

import { useRoute } from '@/hooks/useRoute';

/* =========================================================
   STORE PAGES
   ========================================================= */

import { SiteLayout } from '@/components/layout/SiteLayout';
import { InstagramShowcase } from '@/components/social/InstagramShowcase';

import { HomePage } from '@/pages/HomePage';
import { AccountPage } from '@/pages/AccountPage';
import { CatalogPage } from '@/pages/CatalogPage';
import { ProductPage } from '@/pages/ProductPage';
import { RingBuilderPage } from '@/pages/RingBuilderPage';
import { CollectionsPage } from '@/pages/CollectionsPage';
import { ContentPage } from '@/pages/ContentPage';
import { JournalPage } from '@/pages/JournalPage';

import { WishlistPage } from '@/pages/WishlistPage';
import { ComparePage } from '@/pages/ComparePage';
import { CartPage } from '@/pages/CartPage';
import { CheckoutPage } from '@/pages/CheckoutPage';
import { PaymentResultPage } from '@/pages/PaymentResultPage';

import { DiamondEducationPage } from '@/pages/DiamondEducationPage';
import { RingSizeGuidePage } from '@/pages/RingSizeGuidePage';

import { CJ925ProductPreviewPage } from '@/pages/CJ925ProductPreviewPage';


/* =========================================================
   ADMIN
   ========================================================= */

import { AdminLayout } from '@/admin/AdminLayout';
import { AdminAuthGate } from '@/admin/AdminAuthGate';

import { AdminDashboard } from '@/admin/AdminDashboard';
import { AdminProducts } from '@/admin/AdminProducts';
import { AdminProductEditor } from '@/admin/AdminProductEditor';

import { AdminCJ925Import } from '@/admin/AdminCJ925Import';
import { AdminCJ925PreviewEditor } from '@/admin/AdminCJ925PreviewEditor';

import { AdminHomeContent } from '@/admin/AdminHomeContent';
import { AdminCategories } from '@/admin/AdminCategories';
import { AdminPages } from '@/admin/AdminPages';
import { AdminMedia } from '@/admin/AdminMedia';
import { AdminSettings } from '@/admin/AdminSettings';
import { AdminOrders } from '@/admin/AdminOrders';
import { AdminAnalytics } from '@/admin/AdminAnalytics';
import { AdminSearches } from '@/admin/AdminSearches';


/* =========================================================
   CONTENT PAGES
   ========================================================= */

const contentPages = [
  '/about',
  '/appointments',
  '/lab-diamonds',
  '/shipping',
  '/faq',
  '/contact',
  '/terms',
  '/privacy',
  '/accessibility',
];


/* =========================================================
   APP
   ========================================================= */

export default function App() {
  const route = useRoute();

  /* Remove query string for route matching */
  const path = route.split('?')[0];


  /* =======================================================
     ADMIN ROUTES
     ======================================================= */

  if (path.startsWith('/admin')) {
    let page: ReactNode = <AdminDashboard />;

    if (path === '/admin/products') {
      page = <AdminProducts />;
    }

    else if (path === '/admin/orders') {
      page = <AdminOrders />;
    }

    else if (path === '/admin/analytics') {
      page = <AdminAnalytics />;
    }

    else if (path === '/admin/searches') {
      page = <AdminSearches />;
    }

    else if (path === '/admin/cj925-import') {
      page = <AdminCJ925Import />;
    }

    else if (path === '/admin/products/new') {
      page = <AdminProductEditor />;
    }

    else if (path === '/admin/content/home') {
      page = <AdminHomeContent />;
    }

    else if (path === '/admin/categories') {
      page = <AdminCategories />;
    }

    else if (path === '/admin/pages') {
      page = <AdminPages />;
    }

    else if (path === '/admin/media') {
      page = <AdminMedia />;
    }

    else if (/^\/admin\/cj925-import\/edit\/\d+$/.test(path)) {
      const productId = Number(path.split('/').pop());

      page = (
        <AdminCJ925PreviewEditor
          productId={productId}
        />
      );
    }

    else if (path === '/admin/settings') {
      page = <AdminSettings />;
    }

    else {
      const productMatch = path.match(
        /^\/admin\/products\/(\d+)$/
      );

      if (productMatch) {
        page = (
          <AdminProductEditor
            id={Number(productMatch[1])}
          />
        );
      }
    }

    return (
      <AdminAuthGate>
        <AdminLayout>
          {page}
        </AdminLayout>
      </AdminAuthGate>
    );
  }


  /* =======================================================
     CHECKOUT
     Checkout stays outside normal SiteLayout
     ======================================================= */

  if (path === '/checkout') {
    return <CheckoutPage />;
  }

  if (path === '/payment/success') {
    return <PaymentResultPage kind="success" />;
  }

  if (path === '/payment/error') {
    return <PaymentResultPage kind="error" />;
  }

  if (path === '/payment/cancel') {
    return <PaymentResultPage kind="cancel" />;
  }


  /* =======================================================
     STOREFRONT ROUTES
     ======================================================= */

  let page: ReactNode = <HomePage />;


  /* HOME */

  if (path === '/') {
    page = <HomePage />;
  }


  /* CATALOG */

  else if (path === '/catalog') {
    page = <CatalogPage />;
  }


  /* RING BUILDER */

  else if (path === '/ring-builder') {
    page = <RingBuilderPage />;
  }


  /* COLLECTIONS */

  else if (path === '/collections') {
    page = <CollectionsPage />;
  }


  /* MIELLEY JOURNAL */

  else if (path === '/journal') {
    page = <JournalPage />;
  }


  /* WISHLIST */

  else if (path === '/wishlist') {
    page = <WishlistPage />;
  }


  /* COMPARE */

  else if (path === '/compare') {
    page = <ComparePage />;
  }


  /* CART */

  else if (path === '/cart') {
    page = <CartPage />;
  }


  /* ACCOUNT */

  else if (path === '/account') {
    page = <AccountPage />;
  }


  /* DIAMOND EDUCATION */

  else if (
    path === '/stores' ||
    path === '/guide-4c'
  ) {
    page = <DiamondEducationPage />;
  }


  /* RING SIZE GUIDE */

  else if (
    path === '/size-guide' ||
    path === '/find-ring-sizer'
  ) {
    page = <RingSizeGuidePage />;
  }


  /* CJ925 PREVIEW */

  else if (
    /^\/preview\/cj925\/\d+$/.test(path)
  ) {
    const productId = Number(
      path.split('/').pop()
    );

    page = (
      <CJ925ProductPreviewPage
        productId={productId}
      />
    );
  }


  /* PRODUCT PAGE */

  else {
    const productMatch = path.match(
      /^\/product\/(.+)$/
    );

    if (productMatch) {
      page = (
        <ProductPage
          slug={decodeURIComponent(productMatch[1])}
        />
      );
    }


    /* GENERIC CONTENT PAGES */

    else if (contentPages.includes(path)) {
      page = (
        <ContentPage
          type={path.slice(1)}
        />
      );
    }
  }


  /* =======================================================
     SITE LAYOUT
     ======================================================= */

  return (
    <SiteLayout>
      {page}
      {path === '/' && <InstagramShowcase />}
    </SiteLayout>
  );
}